import { useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { v4 } from "uuid";
import { boolean, number, object, string } from "yup";

import type { LondonCareRecordConfig } from "@thalamos/aspire-common";

import { api, apiHooks } from "~/api.js";
import {
  Button,
  FormLabel,
  HelperText,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";
import { ConfirmationModal } from "~/pages/ConfirmationModal.js";

const londonCareRecordConfigurationSchema = object({
  name: string().min(1).required(),
  expectedServerCommonName: string().nullable(),
  host: string().min(1).required(),
  port: number().min(1).required(),
  urlPath: string().nullable(),
  sslEnabled: boolean().required(),
  ca: string().nullable(),
  cert: string().nullable(),
  key: string().nullable(),
  isTest: boolean().required(),
});

function LondonCareRecordConfigurationInner({
  londonCareRecordConfiguration,
  reload,
}: {
  londonCareRecordConfiguration?: LondonCareRecordConfig;
  reload: () => void;
}) {
  const initialValues = {
    name: londonCareRecordConfiguration?.name ?? "",
    expectedServerCommonName:
      londonCareRecordConfiguration?.expectedServerCommonName ?? "",
    host: londonCareRecordConfiguration?.host ?? "",
    port: londonCareRecordConfiguration?.port,
    urlPath: londonCareRecordConfiguration?.urlPath ?? "",
    sslEnabled: londonCareRecordConfiguration?.sslEnabled,
    ca: londonCareRecordConfiguration?.ca ?? "",
    cert: londonCareRecordConfiguration?.cert ?? "",
    key: londonCareRecordConfiguration?.key ?? "",
    isTest: londonCareRecordConfiguration?.isTest ?? true,
  };

  return (
    <Formik<{
      name: string;
      expectedServerCommonName: string;
      host: string;
      port: number | undefined;
      urlPath: string;
      sslEnabled: boolean | undefined;
      ca: string;
      cert: string;
      key: string;
      isTest: boolean;
    }>
      initialValues={initialValues}
      validationSchema={londonCareRecordConfigurationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        console.log(values);
        try {
          let result;

          if (londonCareRecordConfiguration) {
            result = await api.londonCareRecord.update(
              londonCareRecordConfiguration.id,
              {
                name: values.name,
                host: values.host,
                port: Number(values.port),
                expectedServerCommonName:
                  values.expectedServerCommonName === ""
                    ? null
                    : values.expectedServerCommonName,
                urlPath: values.urlPath === "" ? null : values.urlPath,
                sslEnabled: values.sslEnabled ?? false,
                ...(values.ca && values.ca !== "" ? { ca: values.ca } : {}), // only send if set
                ...(values.cert && values.cert !== ""
                  ? { cert: values.cert }
                  : {}), // only send if set
                ...(values.key && values.key !== "" ? { key: values.key } : {}), // only send if set
                isTest: values.isTest,
              },
            );
          } else {
            result = await api.londonCareRecord.create(v4(), {
              name: values.name,
              host: values.host,
              port: Number(values.port),
              expectedServerCommonName:
                values.expectedServerCommonName === ""
                  ? null
                  : values.expectedServerCommonName,
              urlPath: values.urlPath === "" ? null : values.urlPath,
              sslEnabled: values.sslEnabled ?? false,
              ca: values.ca === "" ? null : values.ca,
              cert: values.cert === "" ? null : values.cert,
              key: values.key === "" ? null : values.key,
              isTest: values.isTest,
            });
          }

          if (result.status === 204) {
            renderSuccessToast({
              message: londonCareRecordConfiguration
                ? "London Care Record configuration updated successfully."
                : "London Care Record configuration created successfully.",
            });
          } else {
            renderErrorToast({
              message: londonCareRecordConfiguration
                ? "Failed to update London Care Record configuration."
                : "Failed to create London Care Record configuration.",
            });
          }
        } catch {
          renderErrorToast({
            message: londonCareRecordConfiguration?.name
              ? `Failed to update London Care Record configuration for ${londonCareRecordConfiguration.name}`
              : "Failed to create London Care Record configuration",
          });
        }
        setSubmitting(false);
        reload();
      }}
    >
      {(formikData) => {
        const { values, setValues, errors, touched, submitForm } = formikData;

        return (
          <Box
            sx={{
              maxWidth: "600px",
              marginBottom: "2em",
              gap: "1em",
              mt: "2em",
            }}
          >
            <Typography variant="h5">
              London Care Record Configuration
            </Typography>
            <Box sx={{ mt: "2em" }}>
              <FormLabel
                label={"Name (*)"}
                error={touched.name && !!errors.name}
              />
              <TextField
                name={"name"}
                useFullWidth={true}
                value={values.name}
                onChange={(e) => {
                  setValues({
                    ...values,
                    name: e,
                  });
                }}
              />
              {errors.name && touched.name && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.name as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Host (*)"}
                error={touched.host && !!errors.host}
              />
              <TextField
                name={"host"}
                useFullWidth={true}
                value={values.host}
                onChange={(e) => {
                  setValues({
                    ...values,
                    host: e,
                  });
                }}
              />
              {errors.host && touched.host && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.host as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Port (*)"}
                error={touched.port && !!errors.port}
              />
              <TextField
                name={"port"}
                useFullWidth={true}
                value={values.port === undefined ? "" : String(values.port)}
                onChange={(e) => {
                  setValues({
                    ...values,
                    port: e === "" ? undefined : Number(e),
                  });
                }}
              />
              {errors.port && touched.port && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.port as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"SSL Enabled (*)"}
                error={touched.sslEnabled && !!errors.sslEnabled}
              />
              <Select
                name="sslEnabled"
                fullWidth
                value={
                  values.sslEnabled === undefined
                    ? ""
                    : String(values.sslEnabled)
                }
                onChange={(e) => {
                  setValues({
                    ...values,
                    sslEnabled: e.target.value === "true",
                  });
                }}
              >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
              {errors.sslEnabled && touched.sslEnabled && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.sslEnabled as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Expected Server Common Name"}
                error={
                  touched.expectedServerCommonName &&
                  !!errors.expectedServerCommonName
                }
              />
              <TextField
                name={"expectedServerCommonName"}
                useFullWidth={true}
                value={values.expectedServerCommonName}
                onChange={(e) => {
                  setValues({
                    ...values,
                    expectedServerCommonName: e,
                  });
                }}
              />
              {errors.expectedServerCommonName &&
                touched.expectedServerCommonName && (
                  <Box sx={{ mb: 4 }}>
                    <HelperText
                      errorMessage={errors.expectedServerCommonName as string}
                    />
                  </Box>
                )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Url Path"}
                error={touched.urlPath && !!errors.urlPath}
              />
              <TextField
                name={"urlPath"}
                useFullWidth={true}
                value={values.urlPath}
                onChange={(e) => {
                  setValues({
                    ...values,
                    urlPath: e,
                  });
                }}
              />
              {errors.urlPath && touched.urlPath && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.urlPath as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Is Test (*)"}
                error={touched.isTest && !!errors.isTest}
              />
              <Select
                name="isTest"
                fullWidth
                value={values.isTest === undefined ? "" : String(values.isTest)}
                onChange={(e) => {
                  setValues({
                    ...values,
                    isTest: e.target.value === "true",
                  });
                }}
              >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
              {errors.isTest && touched.isTest && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.isTest as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Ca - OBFUSCATED"}
                error={touched.ca && !!errors.ca}
              />
              <TextField
                name={"ca"}
                useFullWidth={true}
                multiline={true}
                rows="10"
                value={values.ca}
                onChange={(e) => {
                  setValues({
                    ...values,
                    ca: e,
                  });
                }}
              />
              {errors.ca && touched.ca && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.ca as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Cert - OBFUSCATED"}
                error={touched.cert && !!errors.cert}
              />
              <TextField
                name={"cert"}
                useFullWidth={true}
                multiline={true}
                rows="10"
                value={values.cert}
                onChange={(e) => {
                  setValues({
                    ...values,
                    cert: e,
                  });
                }}
              />
              {errors.cert && touched.cert && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.cert as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Key - OBFUSCATED"}
                error={touched.key && !!errors.key}
              />
              <TextField
                name={"key"}
                useFullWidth={true}
                multiline={true}
                rows="10"
                value={values.key}
                onChange={(e) => {
                  setValues({
                    ...values,
                    key: e,
                  });
                }}
              />
              {errors.key && touched.key && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.key as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <Button
                onClick={submitForm}
                label={londonCareRecordConfiguration ? "Submit" : "Create"}
                type="submit"
              />
              {londonCareRecordConfiguration && (
                <DeleteButton
                  londonCareRecordConfiguration={londonCareRecordConfiguration}
                  reload={reload}
                />
              )}
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
}

function DeleteButton({
  londonCareRecordConfiguration,
  reload,
}: {
  londonCareRecordConfiguration: LondonCareRecordConfig;
  reload: () => void;
}) {
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  return (
    <>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => setConfirmFn(null)}
        />
      )}

      <Button
        onClick={() => {
          setConfirmFn({
            message: `Are you sure you want to delete this london care record configuration ${londonCareRecordConfiguration.name}? This cannot be undone.`,
            confirmFn: async () => {
              try {
                const result = await api.londonCareRecord.delete(
                  londonCareRecordConfiguration.id,
                );

                if (result.status === 204) {
                  renderSuccessToast({
                    message: `London care record configuration ${londonCareRecordConfiguration.name} has been deleted`,
                  });
                  reload();
                } else {
                  renderErrorToast({
                    message: `Failed to remove london care record configuration: ${londonCareRecordConfiguration.name}`,
                  });
                }
              } catch {
                const message = `Failed to remove london care record configuration: ${londonCareRecordConfiguration.name}`;

                renderErrorToast({
                  message,
                });
              }
              setConfirmFn(null);
            },
          });
        }}
        sx={{ ml: "1em" }}
        color={"error"}
        label={"Delete"}
      />
    </>
  );
}

export function LondonCareRecordConfigurationPage() {
  const [
    {
      data: londonCareRecordConfiguration,
      loading: londonCareRecordConfigurationLoading,
    },
    reload,
  ] = apiHooks.londonCareRecord.getLondonCareRecordConfiguration();

  return londonCareRecordConfigurationLoading ? (
    <CircularProgress />
  ) : (
    <LondonCareRecordConfigurationInner
      londonCareRecordConfiguration={londonCareRecordConfiguration}
      reload={reload}
    />
  );
}
